import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { YodiMark } from '@/components/brand/YodiMark';
import { LanguageSelector } from '@/components/ui/language-selector';
import { useApp } from '@/hooks/use-app-context';
import { pageTitle, useDocumentTitle } from '@/hooks/use-document-title';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { authShellCopy, type AuthShellCopy } from './auth-shell.copy';
import { AuthPhotoPanel } from './AuthPhotoPanel';
import './auth-shell.css';

type AuthShellProps = {
  /** Page name for the browser tab, the brand is appended. */
  documentTitle: string;
  children: ReactNode;
};

/**
 * One frame for every authentication screen: the photo panel with the live
 * platform figures on the left (from md up), the form on the right.
 */
export function AuthShell({ documentTitle, children }: AuthShellProps) {
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang === 'en' ? 'en' : 'fr';
  const copy = authShellCopy(currentLang);
  // Same format as the public pages' titles (RouteDocumentTitle).
  useDocumentTitle(pageTitle(documentTitle));

  return (
    <div className="auth-root flex min-h-screen bg-background text-foreground" data-testid="auth-shell">
      <AuthPhotoPanel copy={copy} lang={currentLang} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 px-4 pt-4 sm:px-8 sm:pt-6">
          <Link
            to={`/${currentLang}`}
            className="inline-flex items-center gap-2.5 rounded-xl text-base font-semibold tracking-[-0.02em] text-foreground focus-ring md:hidden"
          >
            <YodiMark className="size-8 text-sm" />
            <span>{copy.brand}</span>
          </Link>
          <BackHomeLink copy={copy} lang={currentLang} className="hidden md:inline-flex" />
          <div className="flex items-center gap-1">
            <LanguageSelector variant="header" />
            <ThemeToggle copy={copy} />
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8 sm:py-12">
          <div className="auth-stagger w-full max-w-[420px] space-y-7">{children}</div>
        </main>

        <footer className="flex justify-center px-4 pb-6 md:hidden">
          <BackHomeLink copy={copy} lang={currentLang} />
        </footer>
      </div>
    </div>
  );
}

function BackHomeLink({ copy, lang, className }: { copy: AuthShellCopy; lang: string; className?: string }) {
  return (
    <Link
      to={`/${lang}`}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-full border border-border/70 px-4 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-ring',
        className,
      )}
    >
      <span aria-hidden="true">←</span>
      {copy.backHome}
    </Link>
  );
}

function ThemeToggle({ copy }: { copy: AuthShellCopy }) {
  const { theme, toggleTheme } = useApp();
  const isDark = theme !== 'light';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? copy.themeToLight : copy.themeToDark}
      title={isDark ? copy.themeToLight : copy.themeToDark}
      className="grid size-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-ring"
    >
      <Icon name={isDark ? 'sun' : 'moon'} className="size-4" />
    </button>
  );
}

/** Page title block: display type, optional eyebrow and lead. */
export function AuthHeading({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-3">
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">{eyebrow}</p>
      ) : null}
      <h1 className="text-balance text-[2rem] font-bold leading-[1.08] tracking-[-0.035em] text-foreground sm:text-[2.35rem]">
        {title}
      </h1>
      {description ? <p className="text-[0.95rem] leading-relaxed text-muted-foreground">{description}</p> : null}
      {children}
    </div>
  );
}

/** A framed message: verification in progress, invalid link, success, account status. */
export function AuthNotice({
  tone = 'neutral',
  icon,
  title,
  children,
  testId,
}: {
  tone?: 'neutral' | 'danger' | 'success' | 'brand';
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  testId?: string;
}) {
  const toneClass = {
    neutral: 'border-border/70 bg-muted/40',
    danger: 'border-danger/30 bg-danger/[0.07]',
    success: 'border-success/30 bg-success/[0.07]',
    brand: 'border-primary/25 bg-primary/[0.06]',
  }[tone];
  const iconClass = {
    neutral: 'text-muted-foreground',
    danger: 'text-danger',
    success: 'text-success',
    brand: 'text-brand',
  }[tone];

  return (
    <div className={cn('flex items-start gap-3.5 rounded-2xl border p-4', toneClass)} data-testid={testId}>
      {icon ? <div className={cn('mt-0.5 shrink-0', iconClass)}>{icon}</div> : null}
      <div className="min-w-0 space-y-1">
        {title ? <p className="text-sm font-semibold text-foreground">{title}</p> : null}
        {children ? <div className="text-sm leading-relaxed text-muted-foreground">{children}</div> : null}
      </div>
    </div>
  );
}

export function AuthSpinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-current/30 border-t-current motion-reduce:animate-none',
        className,
      )}
    />
  );
}

/** Show or hide a password field, with a localized label. */
export function AuthPasswordToggle({
  visible,
  onToggle,
  className,
}: {
  visible: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { lang } = useParams<{ lang: string }>();
  const copy = authShellCopy(lang);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? copy.hidePassword : copy.showPassword}
      aria-pressed={visible}
      className={cn(
        'absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-ring',
        className,
      )}
    >
      <Icon name={visible ? 'eyeOff' : 'eye'} className="size-4" />
    </button>
  );
}

/** Field height, radius and focus shared by every auth input. */
export const AUTH_FIELD_CLASS =
  'h-12 rounded-xl border-border/80 bg-surface text-[0.95rem] shadow-none transition-colors placeholder:text-faint-foreground focus-visible:border-primary/60 focus-visible:ring-[3px] focus-visible:ring-primary/15 focus-visible:ring-offset-0';

/** Primary action of an auth form. */
export const AUTH_PRIMARY_BUTTON_CLASS =
  'h-12 w-full rounded-xl text-[0.95rem] font-semibold shadow-none whitespace-normal';

/** Secondary action of an auth form. */
export const AUTH_SECONDARY_BUTTON_CLASS =
  'h-12 rounded-xl border-border/80 bg-transparent text-[0.95rem] font-medium shadow-none hover:bg-muted/50';
